import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChange,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  LastDealsTableColumn,
  LastDealsTableSorting,
} from '@prlw/core/deals/types/last-deals.types';
import {
  LastDeal,
  TLastDealsSummaryItem,
} from '@prlw/core/deals/entities/last-deal.entity';
import { OverflowState } from '@prlw/libs/overflow-fader/overflow-fader.class';
import { ResponsiveService } from '@prlw/libs/responsive/responsive.service';

type RowHover = {
  offset: string;
  isVisible: boolean;
  index?: number;
  element?: HTMLElement;
};

const PLUGS_LENGTH = 15;
const HOVER_MARGIN = 35;
const ROW_HEIGHT = 36;

@Component({
  selector: 'prlw-last-deals-table-ui',
  templateUrl: './last-deals-table.component.html',
  styleUrls: ['./last-deals-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LastDealsTableComponent implements AfterViewChecked, OnChanges {
  @ViewChild('scroll')
  public scroll?: ElementRef;
  @ViewChild('tableContentWrapper', { static: false })
  public readonly tableContentWrapper?: ElementRef;

  @Input() public data: (LastDeal | TLastDealsSummaryItem)[] | null = null;
  @Input() public columns: LastDealsTableColumn[] | null = null;
  @Input() public sorting: LastDealsTableSorting | null = null;
  @Input() public gridSize: string | null = null;
  @Input() public loading: boolean | null = null;
  @Input() public buttonLoading: boolean | null = null;
  @Input() public overflows: OverflowState | null = null;
  @Input() public isExpanded: boolean | null = null;
  @Input() public fetchMoreLabel: string | null = null;
  @Input() public noDealsAnymore: boolean | null = null;

  @Output() public readonly initResizeObserver = new EventEmitter<{
    elemX: HTMLElement;
    elemY: HTMLElement;
  }>();
  @Output() public readonly setScrollHorizontalPosition =
    new EventEmitter<number>();
  @Output() public readonly setScrollVerticalPosition =
    new EventEmitter<number>();
  @Output() public readonly eventFetchMore = new EventEmitter<void>();

  public readonly loaderPlugsLength = [...Array(PLUGS_LENGTH)];
  private _rowHover!: RowHover;
  private _lastScrollTop: number = 0;

  constructor(private readonly responsiveService: ResponsiveService) {
    this.setRowHover(null);
  }

  xsmall$ = this.responsiveService.xsmall$;

  public ngOnChanges(changes: SimpleChanges): void {
    const data = changes.data as SimpleChange | undefined;
    if (data && data.currentValue?.length !== data.previousValue?.length) {
      this.setRowHover(null);
    }
  }

  private overflowFaderInitialized = false;

  public ngAfterViewChecked(): void {
    if (
      !this.overflowFaderInitialized &&
      this.tableContentWrapper &&
      this.scroll
    ) {
      this.initResizeObserver.emit({
        elemX: this.tableContentWrapper.nativeElement,
        elemY: this.scroll.nativeElement,
      });
      this.overflowFaderInitialized = true;
    }
  }

  public get rowHover(): RowHover {
    return this._rowHover;
  }

  public setHorizontalScroll(event: Event): void {
    const elem = event.target as HTMLElement | null;
    if (elem) {
      this.setScrollHorizontalPosition.emit(elem.scrollLeft);
    }
  }

  public setVerticalScroll(scrollTop: number): void {
    this.setScrollVerticalPosition.emit(scrollTop);
  }

  public processVerticalScroll(event: Event): void {
    const elem = event.target as HTMLElement;
    const scrollTop = elem.scrollTop;
    elem.scrollTop = this._getVerticalScroll(scrollTop);
    this._lastScrollTop = elem.scrollTop;
    this.setVerticalScroll(elem.scrollTop);
  }

  public setRowHover(event: MouseEvent | null, index?: number): void {
    if (this.xsmall$.getValue()) {
      this._rowHover = {
        offset: '0',
        isVisible: false,
      };
      return;
    }
    const elem = event?.target as HTMLElement | undefined;
    const topOffset = Number(elem?.offsetTop);
    const scrollTop = Number(this.scroll?.nativeElement.scrollTop);
    const offsetHeight = Number(this.scroll?.nativeElement.offsetHeight);
    if (this._willRowHoverFit(topOffset, scrollTop, offsetHeight)) {
      this._rowHover = {
        offset: this._calculateRowHoverOffset(topOffset, scrollTop),
        isVisible: true,
        index,
        element: elem,
      };
    } else {
      this._rowHover = {
        offset: '0',
        isVisible: false,
      };
    }
  }

  private _calculateRowHoverOffset(
    topOffset: number,
    scrollTop: number,
  ): string {
    return `${topOffset + HOVER_MARGIN - scrollTop}px`;
  }

  private _willRowHoverFit(
    topOffset: number,
    scrollTop: number,
    offsetHeight: number,
  ): boolean {
    const isTopFit = topOffset >= scrollTop;
    const isBottomFit = topOffset - scrollTop + ROW_HEIGHT <= offsetHeight;
    return isTopFit && isBottomFit;
  }

  private _getVerticalScroll(newScrollValue: number): number {
    if (newScrollValue < this._lastScrollTop) {
      return Math.floor(newScrollValue / ROW_HEIGHT) * ROW_HEIGHT;
    }
    return Math.ceil(newScrollValue / ROW_HEIGHT) * ROW_HEIGHT;
  }

  isSummaryItem(
    item: LastDeal | TLastDealsSummaryItem,
  ): item is TLastDealsSummaryItem {
    return 'totalVolume' in item;
  }
}
